/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import * as font from '@constant/font';
import { IAdminExpressionMetricData } from '@datatype/admin/api-type';
import { useAdminExpressionMetricQuery } from '@hook/admin/useApi';
import TextBlock from '@component/common/block/text-block';
import Spinner from '@design-system/spinner';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 12px;
  width: 280px;
  height: 100%;
  padding: 16px;
  background-color: ${color.WHITE_1};
  border: 1px solid ${color.GRAY_3};
  border-radius: 8px;
  box-sizing: border-box;
  overflow-y: auto;
`;

const Header = styled.div`
  padding-bottom: 8px;
  border-bottom: 1px solid ${color.GRAY_3};
`;

const LanguageSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const LanguageHeader = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 6px;
`;

const LanguageBadge = styled.div`
  min-width: 28px;
  padding: 2px 6px;
  background-color: ${color.BROWN_2};
  border-radius: 4px;
  text-align: center;
`;

const Card = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background-color: ${color.GRAY_4};
  border-radius: 6px;
`;

const ReleaseOrderRow = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 6px;
  padding: 10px 12px;
  background-color: ${color.BROWN_4};
  border-radius: 6px;
`;

const ReleaseOrderText = styled.div`
  font: ${font.FONT_TINY_400};
  color: ${color.BLACK_2};
  white-space: normal;
  overflow-wrap: break-word;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

interface IRowProps {
  label: string;
  value: number;
}

const Row: React.FC<IRowProps> = ({ label, value }) => (
  <Card>
    <TextBlock text={label} design={{ text: { size: 'small', color: color.BLACK_5 } }} />
    <TextBlock
      text={`${value}`}
      design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
    />
  </Card>
);

interface ILanguageMetricProps {
  label: string;
  publishedCount: number;
  scheduledCount: number;
  consumedSourceCount: number;
  remainingSourceCount: number;
}

const LanguageMetric: React.FC<ILanguageMetricProps> = ({
  label,
  publishedCount,
  scheduledCount,
  consumedSourceCount,
  remainingSourceCount,
}) => (
  <LanguageSection>
    <LanguageHeader>
      <LanguageBadge>
        <TextBlock text={label} design={{ text: { size: 'tiny', color: color.WHITE_1, bold: true } }} />
      </LanguageBadge>
    </LanguageHeader>
    <Row label='공개' value={publishedCount} />
    <Row label='예약' value={scheduledCount} />
    <Row label='소비 원본' value={consumedSourceCount} />
    <Row label='남은 원본' value={remainingSourceCount} />
  </LanguageSection>
);

const ExpressionMetricPanel: React.FC = () => {
  const { data, isLoading } = useAdminExpressionMetricQuery();
  const metric: IAdminExpressionMetricData | null = data?.data ?? null;

  if (isLoading || !metric) {
    return (
      <Container>
        <Loader>
          <Spinner size='24px' color={color.BROWN_1} />
        </Loader>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TextBlock
          text='표현 지표'
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true } }}
        />
      </Header>
      <LanguageMetric
        label='EN'
        publishedCount={metric.englishPublishedCount}
        scheduledCount={metric.englishScheduledCount}
        consumedSourceCount={metric.englishConsumedSourceCount}
        remainingSourceCount={metric.englishRemainingSourceCount}
      />
      <LanguageMetric
        label='JA'
        publishedCount={metric.japanesePublishedCount}
        scheduledCount={metric.japaneseScheduledCount}
        consumedSourceCount={metric.japaneseConsumedSourceCount}
        remainingSourceCount={metric.japaneseRemainingSourceCount}
      />
      <ReleaseOrderRow>
        <TextBlock
          text='공개 순서'
          design={{ text: { size: 'small', color: color.BROWN_1, bold: true } }}
        />
        <ReleaseOrderText>
          talk(monologue) → kids → drama → talk(dialogue) → animation → <del>news → drama</del>
        </ReleaseOrderText>
      </ReleaseOrderRow>
    </Container>
  );
};

export default ExpressionMetricPanel;
